import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { Tooltip } from '@/components/ui/Tooltip';
import { ChevronIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon } from '@/components/catalog/icons';
import { PermissionSetFormModal } from '@/components/permissions/PermissionSetFormModal';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import type {
  Paginated,
  PermissionSetDetail,
  PermissionSetListParams,
  PermissionSetSummary,
} from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  type FrozenSide,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/*
 * Permission sets — GET /permission-sets, plus create / rename / retire /
 * delete. The per-set permission grid lives on /permission-sets/:id; this page
 * is the index that gets you there.
 *
 * ── Who can open it ───────────────────────────────────────────────────────
 * SUPER-ADMIN ONLY, and deliberately stricter than the API, which lets any
 * admin write these. Access control is the narrowest surface in the product:
 * an admin who edits a set changes what every other admin can see. So the gate
 * is `ability.isSuperAdmin`, not `ability.isAdmin`, and a non-super-admin gets
 * an explanatory panel with NO request issued at all — an empty grid would
 * read as "there are no sets", which is a different and false statement.
 *
 * A real 403 is still handled below: a role can change under a live token
 * (5.28 days), so the gate passing is not proof the server agrees. A 403 body
 * carries no `details`, so it is a banner and never a field error.
 *
 * ── URL conventions ───────────────────────────────────────────────────────
 * Everything the query depends on lives in the URL, so a filtered view is a
 * link. `useSearchParams()` takes NO argument — its first parameter is a
 * *default init* for the query string, not an options bag, so `replace` goes
 * on each `setSearchParams` call instead. The fetch effect keys off
 * `searchParams.toString()`, never the object, which is a new instance every
 * render.
 *
 * ── The holder count ──────────────────────────────────────────────────────
 * The "how many users hold this set" column is DELIBERATELY OMITTED.
 *
 * `itemCount` (how many permission ROWS a set holds — ⚠️ since 2026-08-30 that
 * is grants AND explicit denials, because unticking a box in the grid stores a
 * `scope: 'none'` row rather than removing one) comes free on the list. The
 * holder count does not, and there is no "users per set" endpoint at all. The
 * only route that answers it is `GET /users/:userId/permission-sets`, which is
 * one request PER USER — so the column would cost 1 (listUsers) + N requests
 * every time this page loads or a filter changes. ⚠️ *SUPERSEDED: "The limiter
 * is 120 requests per 60 seconds per IP."* It is **12,000/60s** (corrected
 * 2026-09-17). **The refusal is the per-user SHAPE — N requests for one column,
 * re-fired on every filter change — not the arithmetic**, and the limiter is
 * still per IP with the whole office behind one NAT. A single `listUsers` pass
 * cannot substitute: `GET /users` does not return each user's sets.
 *
 * The number is not lost — the server knows it, and the delete 409 names it
 * exactly ("still assigned to N users"). That message is surfaced verbatim in
 * the delete dialog, which is the one moment the count actually decides
 * anything.
 */

/** The list endpoint's own default. Its ceiling is 100. */
const DEFAULT_PER_PAGE = 20;
const MAX_PER_PAGE = 100;
const SKELETON_ROWS = 6;

/** The four seeded sets, for the copy that explains what "system" means. */
const SYSTEM_SET_COUNT = 4;

/** Reads a positive integer out of the query string, ignoring anything else. */
function parsePositiveInt(raw: string | null): number | null {
  if (!raw) return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Tri-state: `undefined` means the key is absent and the filter is OFF, which
 * is not the same as `false`. Anything that is not exactly 'true' or 'false' is
 * treated as absent rather than guessed at.
 */
function parseBool(raw: string | null): boolean | undefined {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

interface ParsedQuery {
  page: number;
  perPage: number;
  search: string;
  isActive: boolean | undefined;
  isSystem: boolean | undefined;
}

function parseQuery(params: URLSearchParams): ParsedQuery {
  const perPage = parsePositiveInt(params.get('perPage'));
  return {
    page: parsePositiveInt(params.get('page')) ?? 1,
    // Clamped rather than passed through: >100 is a 422, and a 422 on a page
    // load from someone's stale bookmark is a worse answer than 100 rows.
    perPage: perPage === null ? DEFAULT_PER_PAGE : Math.min(perPage, MAX_PER_PAGE),
    search: params.get('search') ?? '',
    /*
     * `isActive` is ABSENT by default, on purpose. Defaulting it to `true`
     * would hide every retired set, and retiring is this page's recommended
     * alternative to deleting — a retired set nobody can see is a set nobody
     * can restore, rename or reassign. Retired rows show, badged.
     */
    isActive: parseBool(params.get('isActive')),
    isSystem: parseBool(params.get('isSystem')),
  };
}

/** Value for the tri-state filters: '' | 'true' | 'false'. */
function boolValue(value: boolean | undefined): string {
  return value === undefined ? '' : String(value);
}

/*
 * ⚠️ **THE TWO FILTERS BELOW ARE `Combobox`ES, NOT NATIVE `Select`S — owner's
 * instruction, 2026-09-13: every non-table-column filter dropdown in this app is
 * searchable, REGARDLESS OF OPTION COUNT.** Three options do not need a search
 * box; what they need is to behave like the fifteen-option filter on the next
 * screen, so nobody has to learn which dropdowns can be typed into.
 *
 * ⚠️ **THE ABSENT STATE IS `''` IN BOTH PRIMITIVES, AND THAT IS THE ONLY REASON
 * THIS CONVERSION IS BEHAVIOUR-PRESERVING.** `boolValue` already returns `''`
 * for `undefined`, `applyPatch` already drops an empty value from the URL, and a
 * `Combobox` spells "no selection" as `''` + `emptyOptionLabel`. So the default
 * row moves OUT of the option list and INTO `emptyOptionLabel`, and the list
 * below holds only the two narrowing choices.
 *
 * ⚠️ **`emptyOptionLabel` and `placeholder` must carry the SAME string**, which
 * is why each is a named constant rather than two literals. They answer two
 * different questions — what the unselected ROW reads, and what the TRIGGER
 * reads with nothing selected — and a reader who sees "Active and retired" in
 * the list but a blank trigger reads the filter as broken, not as cleared.
 */
const STATUS_ANY_LABEL = 'Active and retired';
const STATUS_OPTIONS: ComboboxOption[] = [
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Retired only' },
];

const ORIGIN_ANY_LABEL = 'System and custom';
const ORIGIN_OPTIONS: ComboboxOption[] = [
  { value: 'true', label: 'System only' },
  { value: 'false', label: 'Custom only' },
];

function SetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="4" y="10.5" width="16" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
      <circle cx="12" cy="15.5" r="1.25" />
    </svg>
  );
}

/**
 * The one thing this page must say out loud.
 *
 * *SUPERSEDED 2026-10-01: "Editing a set changes what this app OFFERS, not what
 * the API permits — every guard on the server is still `requireRole(...)`."*
 * Under the strict model the server enforces the set on every request (only
 * super_admin bypasses), so editing a set changes what people can DO. Info-toned rather than warning: it is a
 * fact about the system, not a problem with the user's action.
 */
function EnforcementNotice() {
  return (
    <Notice tone="info" title={<h2 className="m-0 text-xs font-semibold">What editing a set does</h2>}>
      <p className="m-0">
        Sets decide what people can see <strong>and do</strong> — the server enforces them on every
        request. The app&rsquo;s own menus update at the user&rsquo;s <strong>next sign-in</strong>.
      </p>
    </Notice>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge tone={isActive ? 'success' : 'neutral'} dot>
      {isActive ? 'Active' : 'Retired'}
    </Badge>
  );
}

/**
 * System = one of the four seeded sets (operations, university-partnerships,
 * accounts, management). They CAN be renamed and retired, and they can never be
 * deleted.
 *
 * 🔴 CORRECTED 2026-09-04, TWICE OVER. *SUPERSEDED: the slugs `viewer`,
 * `basic-user`, `advanced-user`, `administrator` (migration 151 renamed them to
 * the business names above), and the clause "They are what `f_role_default_sets`
 * provisions new users with".*
 *
 * That second half was wrong in a way worth keeping, because this file is not
 * where anybody would look for it: `f_role_default_sets` does NOT provision by
 * SLUG. It carries a `permission_set_id` and joins on the id —
 * `user-permission-sets.repository.ts:155`. The belief that the slug was the
 * join key is repeated in several places in both repos and was the stated reason
 * slugs could never be renamed; it is false, and migration 151 renamed them with
 * a guard that aborts the deploy if all five roles stop resolving.
 *
 * ⚠️ AND "provisions new users with" is now the wrong TENSE as well: since
 * 2026-09-04 a user's permissions are DERIVED from their role on every request,
 * not copied once at creation. There is no per-user assignment to provision.
 *
 * The badge is the label and nothing else. It used to carry a `title` on a
 * wrapping span explaining what "system" implies; that went in the 2026-08-30
 * helper-text sweep, because the one consequence a reader has to act on — a
 * system set cannot be deleted — is stated AT the control that refuses it: the
 * row's delete slot renders a dash carrying "System sets cannot be deleted.
 * Retire it instead." That is where somebody looking for the missing button
 * actually is.
 */
function OriginBadge({ isSystem }: { isSystem: boolean }) {
  // A soft neutral fill marks a system set and a quiet outline a custom one —
  // the label carries the meaning. ⚠️ *SUPERSEDED 2026-10-11: `tone="accent"`
  // for system sets*; accent is reserved for primary actions, the active nav row
  // and focus.
  return (
    <Badge tone="neutral" variant={isSystem ? 'soft' : 'outline'}>
      {isSystem ? 'System' : 'Custom'}
    </Badge>
  );
}

/* ── THE TABLE'S COLUMNS ───────────────────────────────────────────────────
 *
 * ONE ordered list, and the header, the body and the skeleton are all generated
 * from it. Before this there were THREE hand-kept six-cell lists that could only
 * be held in step by eye: a column added to the header and forgotten in the
 * skeleton shifts every cell one place sideways while the rows load, and nothing
 * fails — no error, no warning, a table that looks momentarily scrambled.
 *
 * ⚠️ **`width` IS THE PIXEL VALUE OF `headClassName`, AND THE PAIR IS
 * LOAD-BEARING.** The class is what the browser lays out with; the number is
 * what the table's `minWidth` is summed from. They are written side by side so
 * the arithmetic cannot drift from the rendering.
 *
 * ⚠️ **`width: null` MEANS FLUID, and exactly one column carries it — `Set`,
 * which has never had a `w-` class.** It takes whatever the five fixed columns
 * leave. Measured off the header's own classes: 176 + 128 + 112 + 112 + 176 =
 * **704**, and the table's previous `min-w-[900px]` less that is **196**, which
 * is `SET_COLUMN_FLOOR` below. At full visibility the computed floor is
 * therefore still exactly 900 and nothing about the default view moves.
 */
interface SetTableColumn {
  key: string;
  /** The header's text AND the manager's label — one string, so they agree. */
  label: string;
  /** Rendered width in px, or `null` for the one fluid column. */
  width: number | null;
  /** The width class the HEADER cell carries. Body cells take their cue from it. */
  headClassName?: string;
  /** Body-cell classes. Only the count column needs any. */
  cellClassName?: string;
  numeric?: boolean;
}

/** The row-actions lane. Locked — see `LOCKED_COLUMNS`. */
const ACTIONS_COLUMN = 'actions';

const SET_TABLE_COLUMNS: readonly SetTableColumn[] = [
  { key: 'set', label: 'Set', width: null },
  { key: 'slug', label: 'Slug', width: 176, headClassName: 'w-44' },
  {
    /*
     * `grantedCount / permissionTotal` — how many permissions the set grants
     * out of the whole catalogue (2026-10-02, owner's report that every row
     * read 155). Falls back to `itemCount` against an older API. NOT how many
     * people hold it; see the holder-count note at the top of the file.
     */
    key: 'permissions',
    label: 'Permissions',
    width: 128,
    headClassName: 'w-32',
    cellClassName: 'text-fg-secondary tabular',
    numeric: true,
  },
  { key: 'status', label: 'Status', width: 112, headClassName: 'w-28' },
  { key: 'origin', label: 'Origin', width: 112, headClassName: 'w-28' },
  { key: ACTIONS_COLUMN, label: 'Actions', width: 176, headClassName: 'w-44', numeric: true },
];

const COLUMN_KEYS: readonly string[] = SET_TABLE_COLUMNS.map((column) => column.key);

const COLUMN_BY_KEY = new Map(SET_TABLE_COLUMNS.map((column) => [column.key, column] as const));

/**
 * The scope this table's layout is stored under, a sibling of the saved-filter
 * scopes in the account's preferences document.
 */
const COLUMN_LAYOUT_SCOPE = 'permission-sets';

/**
 * ⛔ **ACTIONS IS LOCKED, AND IT IS THE ONLY ONE.**
 *
 * Rename, Retire/Restore and Delete live in that one cell and have no second
 * home anywhere on this page — and the system-set case is worse than a missing
 * button: the reason a system set cannot be deleted is stated ONLY as the
 * `title` on the dash that stands in for its delete control, so hiding the lane
 * hides the explanation with it. `/companies` already paid for this once, where
 * a view that hid the row actions was reported as the feature having been
 * DELETED.
 *
 * ⚠️ **`Set` is deliberately NOT locked, and that is a decision rather than an
 * omission.** It identifies the row, but it is not the only thing that does:
 * `Slug` is unique per set and rendered in full, the Permissions cell carries
 * the same `/permission-sets/:id` link the name does, and every destructive
 * dialog on this page names the set by name (`Delete ${name}?`) whatever is on
 * screen — so nobody can retire or delete a row they cannot identify. Locking it
 * would also forbid MOVING it, because `useColumnLayout` refuses both for a
 * locked key, and a page whose only reason to hide columns is to fit a narrow
 * screen should not have its widest column nailed down.
 */
const LOCKED_COLUMNS: readonly string[] = [ACTIONS_COLUMN];

/**
 * Why the one locked column is locked, in the reader's own terms.
 *
 * ⚠️ **`ColumnManager`'s own default is "This column cannot be hidden", which is
 * true and says nothing.** The real reason here is narrower than a missing
 * button: a system set's delete control is a DASH whose `title` is the only
 * statement anywhere on this page of why that set cannot be deleted, so hiding
 * the lane hides an explanation as well as three controls — see
 * `LOCKED_COLUMNS`.
 *
 * ⚠️ **This is a `title` on a disabled checkbox — a *why this is disabled*
 * sentence, one of the four kinds the no-helper-text prohibition explicitly
 * keeps.** Without it a locked switch and a broken one look identical.
 *
 * ⚠️ **`Set` is deliberately absent, because `Set` is deliberately NOT locked.**
 * Returning a sentence for an unlocked key would be a reason for a restriction
 * that does not exist; `undefined` lets the manager fall back to its own
 * default for anything this map does not name.
 */
function lockReasonOf(key: string): string | undefined {
  return key === ACTIONS_COLUMN
    ? 'Rename, retire and delete live in this column, and so does the reason a system set cannot be deleted'
    : undefined;
}

function columnLabel(key: string): string {
  return COLUMN_BY_KEY.get(key)?.label ?? key;
}

/** See the fluid-column note on `SET_TABLE_COLUMNS`. */
const SET_COLUMN_FLOOR = 196;

/**
 * The table's floor in px for the columns currently visible.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so
 * `min-w-[${n}px]` emits no rule at all — the class lands in the DOM and does
 * nothing, which looks exactly like a table that chose to have no floor.
 *
 * ⚠️ **HIDING A COLUMN HAS TO SHRINK THIS.** A floor left at 900 over three
 * columns is a table that still scrolls sideways with empty space in it: the
 * reader hides columns precisely to stop the horizontal scroll, and the scroll
 * stays.
 */
function tableMinWidth(visibleKeys: readonly string[]): number {
  return visibleKeys.reduce((total, key) => {
    const width = COLUMN_BY_KEY.get(key)?.width;
    // `null` is the fluid column and contributes its FLOOR; an unknown key
    // contributes nothing rather than `NaN`, which would blank the style.
    if (width === null) return total + SET_COLUMN_FLOOR;
    return total + (width ?? 0);
  }, 0);
}

interface FrozenProps {
  frozen?: FrozenSide;
  frozenOffset?: number;
}

/**
 * An unfrozen cell must carry NEITHER key — `Table` only positions a cell when
 * `frozen` is present, and a cell positioned for no reason paints over the
 * table's collapsed borders.
 */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): FrozenProps {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

/** The loading shape of one cell, chosen by column key. */
function skeletonCell(key: string): ReactNode {
  switch (key) {
    case 'set':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-44" />
          <Skeleton className="h-2.5 w-56" />
        </div>
      );
    case 'slug':
      return <Skeleton className="h-3 w-28" />;
    case 'permissions':
      return <Skeleton className="ml-auto h-3 w-10" />;
    case ACTIONS_COLUMN:
      return (
        <div className="flex justify-end gap-1.5">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-6" />
        </div>
      );
    default:
      // Status and Origin are both one badge-sized block.
      return <Skeleton className="h-4 w-16" />;
  }
}

interface ColumnRenderProps {
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}

function TableSkeleton({ visibleKeys, frozenSideOf, frozenOffsets }: ColumnRenderProps) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {visibleKeys.map((key) => (
            <TD
              key={key}
              numeric={COLUMN_BY_KEY.get(key)?.numeric}
              {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
            >
              {skeletonCell(key)}
            </TD>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

/** The pending write, so one dialog can serve both retire and restore. */
type PendingRetire = { set: PermissionSetSummary; next: boolean };

export function PermissionSetsPage() {
  const { ability } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [result, setResult] = useState<Paginated<PermissionSetSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** A 403 is a role change under a live token, not a validation failure. */
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [formState, setFormState] = useState<{ set?: PermissionSetSummary } | null>(null);

  const [pendingDelete, setPendingDelete] = useState<PermissionSetSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /*
   * Whether the last delete failure was one of the three 409s. Kept apart from
   * the message so the "retire instead" offer is composed during RENDER: an
   * element stashed in state at the moment of the failure would freeze
   * `deleting` at whatever it was then, and the button would never show its
   * spinner.
   */
  const [deleteConflict, setDeleteConflict] = useState(false);

  const [pendingRetire, setPendingRetire] = useState<PendingRetire | null>(null);
  const [retiring, setRetiring] = useState(false);
  const [retireError, setRetireError] = useState<string | null>(null);

  /*
   * The gate. `isSuperAdmin` — not `isAdmin`: the API would accept an admin's
   * writes here and this app refuses to offer them.
   */
  /*
   * 🔴 2026-10-01: the `permission-sets` module (only the super-admin set holds
   * it today, so the audience is unchanged). *SUPERSEDED: `ability.isSuperAdmin`.*
   * `canManage` is the READ gate for the page; each control asks its own action.
   */
  const canManage = ability.canRead('permission-sets');
  const canCreateSet = ability.canCreate('permission-sets');
  const canEditSet = ability.canUpdate('permission-sets');
  const canDeleteSet = ability.canDelete('permission-sets');

  /*
   * Column layout — order, hidden set and the two frozen edges — stored against
   * the ACCOUNT rather than in this browser.
   *
   * ⚠️ **It reads `/preferences` for every visitor, the non-super-admin who only
   * ever meets the refusal below included.** A hook cannot be called after the
   * early return, and the alternative — splitting the refusal into its own
   * component — moves the whole page for the sake of one request against the
   * reader's OWN preferences document, which discloses nothing about permission
   * sets. The "no request issued at all" note at the top of this file is about
   * the permission-set LIST and stays true of it.
   */
  const columns = useColumnLayout(COLUMN_LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_COLUMNS);
  const { visibleKeys } = columns;
  const layoutFrozenSideOf = columns.frozenSideOf;

  const queryString = searchParams.toString();
  const query = useMemo(() => parseQuery(new URLSearchParams(queryString)), [queryString]);
  const { page, perPage, search, isActive, isSystem } = query;

  const load = useCallback(
    (signal?: AbortSignal) => {
      const current = parseQuery(new URLSearchParams(queryString));
      setLoading(true);
      const params: PermissionSetListParams = { page: current.page, perPage: current.perPage };
      // Blank keys are omitted rather than sent empty: `?isActive=` is a 422,
      // not "no filter".
      if (current.search) params.search = current.search;
      if (current.isActive !== undefined) params.isActive = current.isActive;
      if (current.isSystem !== undefined) params.isSystem = current.isSystem;
      return api
        .listPermissionSets(params, signal)
        .then((data) => {
          setResult(data);
          setError(null);
          setForbidden(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            /*
             * The token is still valid and the role behind it no longer reaches
             * permission sets. No `details` on a 403, so nothing to hang on a
             * field — the whole surface goes away with a banner saying why.
             */
            setForbidden(
              caught.message ||
                'Your account no longer has permission to manage permission sets.',
            );
            setError(null);
            setResult(null);
            return;
          }
          setForbidden(null);
          setError(caught instanceof Error ? caught.message : 'Failed to load permission sets');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [queryString],
  );

  useEffect(() => {
    // No request at all without super-admin. The explanatory panel below
    // renders instead, and it never reads `loading`.
    if (!canManage) return;
    const controller = new AbortController();
    // 250ms, because the search box writes to the URL as you type and a
    // substring must not cost a request per keystroke.
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [canManage, load]);

  const applyPatch = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === '') next.delete(key);
        else next.set(key, value);
      }
      // Any change to what is being asked for invalidates the offset.
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const goToPage = useCallback(
    (target: number) => {
      applyPatch({ page: target <= 1 ? '' : String(target) });
    },
    [applyPatch],
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;
  const anyFilter = search !== '' || isActive !== undefined || isSystem !== undefined;

  /*
   * ── FREEZING ───────────────────────────────────────────────────────────
   *
   * A NEW `frozenSideOf` IDENTITY WHEN THE TABLE SWAPS ITS BODY.
   *
   * ⚠️ **KEPT, BUT ITS ORIGINAL REASON IS SPENT — read the correction rather
   * than the claim.** *SUPERSEDED: "the extra dependency is the whole point of
   * this wrapper … Without this, a SECOND column frozen to the same edge keeps
   * the skeleton's offset for ever and overlaps its neighbour by the
   * difference."* `useFrozenOffsets` tracks the header ROW's element identity
   * itself as of 2026-09-12, and the skeleton and the rows are two `Table`
   * mounts with two different rows — so the hook re-measures the swap on its
   * own and this wrapper is no longer what saves it. The hook's own header names
   * this page's version of the workaround as one of the three it absorbed.
   *
   * ⚠️ **It is left in place because it is CORRECT and removing it is a change
   * in the risky direction.** The fact it was written for is unchanged: under
   * AUTO layout a `w-*` on a `<th>` is a request the content can outvote, so the
   * widths the skeleton produced are not the widths the rows produce. A stale
   * offset does not error — two frozen columns simply sit on top of each other —
   * so a second re-measure on that commit costs nothing and buys the belt to the
   * hook's braces. **Do not reinstate the deleted sentence if you keep this.**
   */
  const tableBody = initialLoad ? 'skeleton' : 'rows';
  const frozenSideOf = useCallback(
    (key: string) => layoutFrozenSideOf(key),
    [layoutFrozenSideOf, tableBody],
  );

  /*
   * The header row, for `useFrozenOffsets`.
   *
   * ✅ **AN ORDINARY `ref` ON `TR`.** *SUPERSEDED: "FOUND BY QUERY RATHER THAN
   * BY A `ref` ON `TR`, and that is a TYPE constraint rather than a preference —
   * `TRProps` extends `HTMLAttributes`, which declares no `ref` … `Table.tsx` is
   * shared and out of scope here."* `TRProps` declares
   * `ref?: Ref<HTMLTableRowElement>` as of 2026-09-12, so the `querySelector`
   * off an `aria-busy` wrapper, the layout effect that ran it on every render,
   * and the wrapper ref it needed are all gone.
   *
   * ⚠️ **With it goes the hook-ordering constraint the old note ended on.**
   * React attaches a host element's ref in the commit's layout phase, child
   * before parent, so the row is attached before any layout effect declared in
   * THIS component — including on the commit where the skeleton gives way to the
   * rows. Nothing here depends on declaration order any more.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);

  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);

  /** See `tableMinWidth` — a number through `style`, never a computed class. */
  const tableStyle = useMemo(
    () => ({ minWidth: `${tableMinWidth(visibleKeys)}px` }),
    [visibleKeys],
  );

  /** Retire (`isActive: false`) or restore (`isActive: true`). */
  async function confirmRetire() {
    if (!pendingRetire) return;
    setRetiring(true);
    setRetireError(null);
    try {
      await api.updatePermissionSet(pendingRetire.set.id, { isActive: pendingRetire.next });
      setPendingRetire(null);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setRetireError(
          caught.message || 'Your account no longer has permission to change permission sets.',
        );
      } else {
        setRetireError(caught instanceof Error ? caught.message : 'The change could not be saved');
      }
    } finally {
      setRetiring(false);
    }
  }

  /**
   * Retire straight from the delete dialog's 409.
   *
   * All three 409s say the same thing — retire it instead — so the way to do
   * that is offered right where the refusal is read, rather than making the
   * user dismiss the dialog and hunt for the button.
   */
  async function retireFromDeleteRefusal(set: PermissionSetSummary) {
    setDeleting(true);
    try {
      await api.updatePermissionSet(set.id, { isActive: false });
      setPendingDelete(null);
      setDeleteError(null);
      setDeleteConflict(false);
      await load();
    } catch (caught) {
      setDeleteConflict(false);
      setDeleteError(caught instanceof Error ? caught.message : 'The set could not be retired');
    } finally {
      setDeleting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const set = pendingDelete;
    setDeleting(true);
    setDeleteError(null);
    setDeleteConflict(false);
    try {
      await api.deletePermissionSet(set.id);
      setPendingDelete(null);
      if (rows.length === 1 && page > 1) goToPage(page - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        /*
         * Three cases — system set, still assigned to N users, race backstop —
         * and the server's message names which one and the count. It is better
         * copy than anything invented here, so it is surfaced VERBATIM, and the
         * alternative all three recommend is offered right beside it.
         */
        setDeleteConflict(true);
        setDeleteError(caught.message);
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — someone else deleted it. Nothing to report.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) goToPage(page - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        setDeleteError(
          caught.message || 'Your account no longer has permission to delete permission sets.',
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved(saved: PermissionSetDetail) {
    const wasCreate = !formState?.set;
    setFormState(null);
    /*
     * The modal hands back the committed set, so the visible row is patched
     * from it immediately — a rename should not wait on a round trip. The list
     * is still re-read afterwards, because ordering, paging and the active
     * filters are all server-side and a renamed or retired set can legitimately
     * belong on a different page (or on none of them). `items` is dropped: this
     * list's rows are summaries and `itemCount` already carries the number.
     */
    if (!wasCreate) {
      setResult((current) =>
        current
          ? {
              ...current,
              data: current.data.map((row) => {
                if (row.id !== saved.id) return row;
                const { items: _items, ...summary } = saved;
                return summary;
              }),
            }
          : current,
      );
    }
    // A create goes back to page 1 so the new row is somewhere it can be seen.
    if (wasCreate && page !== 1) goToPage(1);
    else void load();
  }

  /**
   * One body cell, chosen by column key — the same keys the header and the
   * skeleton read, so a column can never appear in one and not the others.
   *
   * Declared inside the component rather than at module scope because four of
   * the six cells carry a control that writes this page's state.
   */
  function renderCell(key: string, set: PermissionSetSummary): ReactNode {
    switch (key) {
      case 'set':
        return (
          <div className="min-w-0">
            <TextLink
              to={`/permission-sets/${set.id}`}
              className="text-sm font-medium [overflow-wrap:anywhere]"
            >
              {set.name}
            </TextLink>
            {set.description && (
              <p className="mt-0.5 truncate text-xs text-fg-muted">{set.description}</p>
            )}
          </div>
        );
      case 'slug':
        return (
          <code className="text-xs text-fg-secondary [overflow-wrap:anywhere]">{set.slug}</code>
        );
      case 'permissions': {
        // Granted out of the whole catalogue. Every seeded set holds an
        // explicit row for every permission (migration 250), so itemCount
        // alone read the catalogue size on every row. Against an API without
        // the two fields, fall back to the old figure rather than a 0.
        const granted = set.grantedCount;
        const total = set.permissionTotal;
        const hasRatio = granted !== undefined && total !== undefined && total > 0;
        const label = hasRatio
          ? `${set.name} grants ${granted} of ${total} permissions — edit them`
          : `Edit the ${set.itemCount} permissions in ${set.name}`;
        return (
          <Tooltip label={label}>
            <TextLink to={`/permission-sets/${set.id}`} aria-label={label}>
              {hasRatio ? (
                <>
                  {granted}
                  <span className="text-fg-muted"> / {total}</span>
                </>
              ) : (
                set.itemCount
              )}
            </TextLink>
          </Tooltip>
        );
      }
      case 'status':
        return <StatusBadge isActive={set.isActive} />;
      case 'origin':
        return <OriginBadge isSystem={set.isSystem} />;
      case ACTIONS_COLUMN:
        return (
          <div className="flex flex-wrap justify-end gap-1.5">
            {canEditSet && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                leadingIcon={<PencilIcon />}
                aria-label={`Rename ${set.name}`}
                title={`Rename ${set.name}`}
                onClick={() => setFormState({ set })}
              />
            )}
            {canEditSet && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setRetireError(null);
                  setPendingRetire({ set, next: !set.isActive });
                }}
              >
                {set.isActive ? 'Retire' : 'Restore'}
              </Button>
            )}
            {/*
              * No delete control on a system set at all. It would always 409 —
              * a role provisions new users with these four — and a button whose
              * only outcome is a refusal is worse than no button.
              */}
            {!canDeleteSet ? null : set.isSystem ? (
              <span
                className="grid h-7 w-7 place-items-center text-xs text-fg-subtle"
                title="System sets cannot be deleted. Retire it instead."
                aria-hidden="true"
              >
                —
              </span>
            ) : (
              <Button
                variant="danger"
                size="sm"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${set.name}`}
                title={`Delete ${set.name}`}
                onClick={() => {
                  setDeleteError(null);
                  setPendingDelete(set);
                }}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  }

  /* ── Not a super-admin: explain, and never fetch ───────────────────────── */

  if (!canManage) {
    return (
      <div className="grid min-w-0 gap-4">
        {/*
         * No description here. The "this shapes what the app OFFERS, not what
         * the API allows" claim is NOT lost with it: `EnforcementNotice` is on
         * the main branch only, but the EmptyState below carries the same point
         * for this branch — "a tighter rule than the API itself applies" — and
         * a reader who cannot open the screen has no edit to misjudge.
         */}
        <PageHeader eyebrow="Access control" title="Permission sets" />
        <Panel aria-labelledby="permission-sets-denied-heading">
          <PanelHeader id="permission-sets-denied-heading" title="Permission sets" />
          <EmptyState
            icon={<SetIcon />}
            title="Not available to your account"
            description="Only super-administrators can change permission sets."
          />
        </Panel>
      </div>
    );
  }

  /* ── The page ──────────────────────────────────────────────────────────── */

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'set' : 'sets'}${search ? ` matching “${search}”` : ''}`;

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow="Access control"
        title="Permission sets"
        actions={
          canCreateSet && (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
              New set
            </Button>
          )
        }
      />

      <EnforcementNotice />

      <Panel className="min-w-0" aria-labelledby="permission-sets-heading">
        <PanelHeader
          id="permission-sets-heading"
          title="All sets"
          description={countLabel}
        />

        {/*
          * Filters get their own full-width row rather than PanelHeader's
          * `actions` slot: that slot is `shrink-0`, so its contents can neither
          * shrink nor wrap and a three-control bank takes the page sideways
          * below 768px. This row wraps freely and works at 320px.
          */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search permission sets" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  // The endpoint matches name OR slug, so the placeholder can
                  // promise both without the results contradicting it.
                  placeholder="Search name or slug…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => applyPatch({ search: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            <Field label="Filter by status" hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={boolValue(isActive)}
                  onChange={(next) => applyPatch({ isActive: next })}
                  options={STATUS_OPTIONS}
                  /* Default. Retired sets stay visible — see parseQuery. */
                  emptyOptionLabel={STATUS_ANY_LABEL}
                  placeholder={STATUS_ANY_LABEL}
                  searchPlaceholder="Search statuses…"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            <Field label="Filter by origin" hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={boolValue(isSystem)}
                  onChange={(next) => applyPatch({ isSystem: next })}
                  options={ORIGIN_OPTIONS}
                  emptyOptionLabel={ORIGIN_ANY_LABEL}
                  placeholder={ORIGIN_ANY_LABEL}
                  searchPlaceholder="Search origins…"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
          {/*
            * ⚠️ **OUTSIDE `TableWrap`, AND THAT IS THE WHOLE POINT ON THIS PAGE.**
            * There is no sub-breakpoint card fallback here — below the table's
            * floor the table is simply a horizontal scroller — so hiding columns
            * is the only way to make this fit a narrow screen. A trigger placed
            * inside the scroller would be the control you have to scroll
            * sideways to reach in order to stop scrolling sideways. This row
            * wraps freely and every control in it is reachable at 320px.
            *
            * It sits BEFORE `Clear filters` so its position does not move as
            * that button appears and disappears with the filters.
            */}
          {/*
            ⚠️ **NO `pinnedKeys`, and that is a statement rather than an
            omission.** `pinnedKeys` names the columns the page renders with
            `pinned` — `sticky right-0` — whose edge is therefore already
            decided. **This table renders no `pinned` cell**: the actions lane is
            an ordinary last column, so a reader may freeze it like any other.
            Naming it here would remove its freeze controls and make
            `useFrozenOffsets` reserve width at an edge nothing occupies.

            ⚠️ **`pinnedKeys` is NOT `lockedKeys`.** Locked is about hiding and
            reordering, pinned about which edge a cell sits at — `actions` is
            locked and still freezable.
          */}
          <ColumnManager
            layout={columns}
            labelOf={columnLabel}
            lockedKeys={LOCKED_COLUMNS}
            lockReasonOf={lockReasonOf}
          />
          {anyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {forbidden && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title="Your account can no longer manage permission sets."
          >
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">
              {forbidden} Sign out and back in to pick up the change.
            </p>
          </Notice>
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title={<>Couldn&rsquo;t load permission sets.</>}
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {initialLoad ? (
              <TableWrap frozenHeader>
                {/* The floor is `tableStyle`, shared with the real table below, so
                    the two can no longer disagree about the table's width the way
                    two copies of a `min-w-` literal did. It sits on `Table`
                    itself now that the primitive takes a `style`; it used to need
                    a spacer `<div>` here purely to carry the number. */}
                <Table style={tableStyle}>
                  <SetsTableHead
                    visibleKeys={visibleKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  <TableSkeleton
                    visibleKeys={visibleKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </Table>
              </TableWrap>
            ) : rows.length === 0 ? (
              <SetsEmptyState
                filtered={anyFilter}
                hasError={Boolean(error)}
                onClear={clearFilters}
                onCreate={() => setFormState({})}
              />
            ) : (
              <TableWrap frozenHeader>
                {/* Kept in step with the skeleton above by construction: the same
                    `tableStyle` and the same `visibleKeys` feed both. */}
                <Table style={tableStyle}>
                  <SetsTableHead
                    visibleKeys={visibleKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  <TBody>
                    {rows.map((set) => (
                      <TR key={set.id} interactive>
                        {visibleKeys.map((key) => (
                          <TD
                            key={key}
                            numeric={COLUMN_BY_KEY.get(key)?.numeric}
                            className={COLUMN_BY_KEY.get(key)?.cellClassName}
                            {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                          >
                            {renderCell(key, set)}
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
        )}

        {!forbidden && result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No sets to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <PermissionSetFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        set={formState?.set}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={pendingRetire !== null}
        onClose={() => {
          setPendingRetire(null);
          setRetireError(null);
        }}
        onConfirm={() => void confirmRetire()}
        tone={pendingRetire?.next ? 'primary' : 'danger'}
        title={
          pendingRetire
            ? pendingRetire.next
              ? `Restore ${pendingRetire.set.name}?`
              : `Retire ${pendingRetire.set.name}?`
            : 'Retire permission set?'
        }
        description={
          pendingRetire?.next
            ? 'Makes the set assignable again.'
            : 'Stops new assignments. Anyone already holding it keeps it. Reversible.'
        }
        confirmLabel={pendingRetire?.next ? 'Restore set' : 'Retire set'}
        loading={retiring}
        error={retireError}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
          setDeleteConflict(false);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete permission set?'}
        description={
          <>
            <p className="m-0">
              The set and its {pendingDelete?.itemCount ?? 0}{' '}
              {pendingDelete?.itemCount === 1 ? 'permission' : 'permissions'} will be deleted. This
              can't be undone.
            </p>
            <p className="mt-2 mb-0">
              A set that anyone still holds can't be deleted. Retire it instead.
            </p>
          </>
        }
        confirmLabel="Delete set"
        loading={deleting}
        error={
          deleteError && (
            <>
              {/* Verbatim. It names the reason and, when that reason is
                  assignments, the exact number of people still holding it. */}
              <span className="[overflow-wrap:anywhere]">{deleteError}</span>
              {deleteConflict && pendingDelete?.isActive && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-2 align-middle"
                  loading={deleting}
                  onClick={() => void retireFromDeleteRefusal(pendingDelete)}
                >
                  Retire it instead
                </Button>
              )}
            </>
          )
        }
      />
    </div>
  );
}

/**
 * ✅ **The row carries the `ref` `useFrozenOffsets` measures.** *SUPERSEDED: "No
 * `ref` on this row — `useFrozenOffsets` needs the header row element and finds
 * it by query from the page."* `TR` takes a `ref` as of 2026-09-12, so the
 * element is handed over directly instead of being hunted for with a
 * `querySelector` in a layout effect.
 *
 * ⚠️ **It belongs on the ROW and not on a wrapper**: the hook reads the frozen
 * columns' offsets off this row's CHILDREN, cell by cell, in visible-key order.
 */
function SetsTableHead({
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
}: ColumnRenderProps & { headRowRef: RefObject<HTMLTableRowElement | null> }) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {visibleKeys.map((key) => {
          const column = COLUMN_BY_KEY.get(key);
          // A stored order can name a column this table no longer declares; it
          // renders nothing rather than an empty cell that shifts the row.
          if (column === undefined) return null;
          return (
            <TH
              key={key}
              numeric={column.numeric}
              className={column.headClassName}
              {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
            >
              {column.label}
            </TH>
          );
        })}
      </TR>
    </THead>
  );
}

function SetsEmptyState({
  filtered,
  hasError,
  onClear,
  onCreate,
}: {
  filtered: boolean;
  hasError: boolean;
  onClear: () => void;
  onCreate: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<SetIcon />}
        title="Nothing to show"
        description="Couldn’t load the list — see the message above."
      />
    );
  }

  if (filtered) {
    return (
      <EmptyState
        icon={<SetIcon />}
        title="No sets match those filters"
        description="Try a different term, or clear the filters."
        action={
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        }
      />
    );
  }

  /*
   * Genuinely unusual: the product seeds four system sets, so an unfiltered
   * empty list means they were removed from the database rather than that
   * nobody has made one yet. The copy says so instead of cheerfully inviting a
   * first set.
   */
  return (
    <EmptyState
      icon={<SetIcon />}
      title="No permission sets at all"
      description={`None of the ${SYSTEM_SET_COUNT} system sets are here, so new users get nothing. Create one, or re-run the seed.`}
      action={
        <Button variant="secondary" size="sm" leadingIcon={<PlusIcon />} onClick={onCreate}>
          New set
        </Button>
      }
    />
  );
}
